import {
  useEffect,
  useState,
  type KeyboardEventHandler,
  type PointerEventHandler
} from 'react';
import { Link } from 'react-router-dom';

import type { QueueActivityEvent, QueueItem, RunActivityTree } from '../types';
import {
  extractActivityNodePreview,
  formatActivityDuration,
  humanizeRunnerStatus,
  isAgentContainerStep,
  renderTextWithLinks,
  stageLabelForId
} from '../utils/conversationRunActivity';
import { Timestamp } from './Timestamp';
import type { ApiClient } from '../api/client';
import { ConversationFenceTrace } from './ConversationFenceTrace';

type ConversationActivitySidebarProps = {
  apiClient?: ApiClient;
  show: boolean;
  conversationId: string;
  selectedActivityQueueItem: QueueItem | null;
  selectedActivityTree: RunActivityTree | null;
  selectedActivityLoading: boolean;
  selectedActivityLoadingOlder: boolean;
  selectedActivityRunId: string | null;
  selectedActivityRunCwd?: string | null;
  effectiveSelectedActivityMessageId: string | null;
  queueActivityLoading: boolean;
  selectedPreparationActivityGrouped: QueueActivityEvent[];
  selectedQueueActivityGrouped: QueueActivityEvent[];
  messagePreviewById: Map<string, string>;
  expandedActivityStages: Record<string, boolean>;
  expandedActivityCommands: Record<string, boolean>;
  visibleActivityStagesByMessage: Record<string, number>;
  visibleActivityCommandsByStage: Record<string, number>;
  visibleActivityOutputLinesByCommand: Record<string, number>;
  loadingOlderCommandOutput: Record<string, boolean>;
  copyingRawCommandOutput: Record<string, boolean>;
  activityStagesPage: number;
  activityStageCommandsPage: number;
  activityOutputLinesPage: number;
  onClose: () => void;
  onStartResize: PointerEventHandler<HTMLDivElement>;
  onResize: PointerEventHandler<HTMLDivElement>;
  onFinishResize: PointerEventHandler<HTMLDivElement>;
  onResizeKeyDown: KeyboardEventHandler<HTMLDivElement>;
  onToggleStage: (stageKey: string, isExpanded: boolean) => void;
  onToggleCommand: (
    commandKey: string,
    isExpanded: boolean,
    outputVisibleKey: string,
    loadedOutputCount: number
  ) => void;
  onShowMoreOutput: (outputVisibleKey: string, loadedOutputCount: number) => void;
  onShowMoreCommands: (stageVisibleCommandCountKey: string, commandCount: number) => void;
  onShowMoreStages: (stageVisibleCountKey: string, stageCount: number) => void;
  onLoadOlderCommandOutput: (messageId: string, runId: string, commandId: string) => void;
  onCopyRawAgentCommandOutput: (messageId: string, runId: string, commandId: string) => void;
  onLoadOlderActivityCommands: (messageId: string, runId: string) => void;
  messageVisibleStageCountKey: (messageId: string) => string;
  stageExpandKey: (messageId: string, stageId: string) => string;
  stageVisibleCommandCountKey: (messageId: string, stageId: string) => string;
  commandExpandKey: (messageId: string, commandId: string) => string;
  commandVisibleOutputLineCountKey: (messageId: string, commandId: string) => string;
};

const countOutputStreams = (output: { stream: string }[]) => ({
  stdout: output.filter((line) => line.stream === 'stdout').length,
  stderr: output.filter((line) => line.stream === 'stderr').length
});

export function ConversationActivitySidebar({
  apiClient,
  show,
  conversationId,
  selectedActivityQueueItem,
  selectedActivityTree,
  selectedActivityLoading,
  selectedActivityLoadingOlder,
  selectedActivityRunId,
  selectedActivityRunCwd,
  effectiveSelectedActivityMessageId,
  queueActivityLoading,
  selectedPreparationActivityGrouped,
  selectedQueueActivityGrouped,
  messagePreviewById,
  expandedActivityStages,
  expandedActivityCommands,
  visibleActivityStagesByMessage,
  visibleActivityCommandsByStage,
  visibleActivityOutputLinesByCommand,
  loadingOlderCommandOutput,
  copyingRawCommandOutput,
  activityStagesPage,
  activityStageCommandsPage,
  activityOutputLinesPage,
  onClose,
  onStartResize,
  onResize,
  onFinishResize,
  onResizeKeyDown,
  onToggleStage,
  onToggleCommand,
  onShowMoreOutput,
  onShowMoreCommands,
  onShowMoreStages,
  onLoadOlderCommandOutput,
  onCopyRawAgentCommandOutput,
  onLoadOlderActivityCommands,
  messageVisibleStageCountKey,
  stageExpandKey,
  stageVisibleCommandCountKey,
  commandExpandKey,
  commandVisibleOutputLineCountKey
}: ConversationActivitySidebarProps) {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!show) {
      return;
    }
    const interval = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [show]);

  if (!show) {
    return null;
  }

  const showActivityMeta =
    (selectedActivityQueueItem?.status === 'queued' &&
      selectedActivityQueueItem.waiting_for_count > 0) ||
    Boolean(selectedActivityTree?.has_more_commands) ||
    (selectedActivityLoading && !selectedActivityTree) ||
    queueActivityLoading ||
    selectedActivityLoadingOlder ||
    (!effectiveSelectedActivityMessageId && !selectedActivityLoading && !selectedActivityTree);

  return (
    <>
      <div
        className="runner-sidebar-resizer"
        role="separator"
        aria-label="Resize turn activity panel"
        aria-orientation="vertical"
        tabIndex={0}
        onPointerDown={onStartResize}
        onPointerMove={onResize}
        onPointerUp={onFinishResize}
        onPointerCancel={onFinishResize}
        onKeyDown={onResizeKeyDown}
      />
      <aside className="runner-sidebar" aria-live="polite">
        <div className="runner-sidebar__header">
          <div className="runner-sidebar__title">Turn activity</div>
          <button type="button" className="ghost" onClick={onClose}>
            Close
          </button>
        </div>
        {apiClient && selectedActivityRunId && (
          <ConversationFenceTrace
            key={`${conversationId}:${selectedActivityRunId}`}
            apiClient={apiClient}
            conversationId={conversationId}
            runId={selectedActivityRunId}
          />
        )}
        {showActivityMeta && (
          <div className="runner-sidebar__meta">
            {selectedActivityQueueItem?.status === 'queued' &&
              selectedActivityQueueItem.waiting_for_count > 0 && (
                <div className="status">
                  Waiting for {selectedActivityQueueItem.waiting_for_count}{' '}
                  {selectedActivityQueueItem.waiting_for_count === 1
                    ? 'earlier turn'
                    : 'earlier turns'}
                  {(selectedActivityQueueItem.waiting_for_message_ids?.length ?? 0) > 0 && (
                    <span>
                      {' · '}
                      {selectedActivityQueueItem.waiting_for_message_ids
                        ?.slice(0, 3)
                        .map((blockerId) => messagePreviewById.get(blockerId) ?? blockerId)
                        .join(' | ')}
                    </span>
                  )}
                </div>
              )}
            {selectedActivityTree?.has_more_commands && (
              <div className="status">Older activity available</div>
            )}
            {selectedActivityLoading && !selectedActivityTree && (
              <div className="status">Loading activity tree…</div>
            )}
            {queueActivityLoading && <div className="status">Loading queue activity…</div>}
            {selectedActivityLoadingOlder && (
              <div className="status">Loading older activity…</div>
            )}
            {!effectiveSelectedActivityMessageId &&
              !selectedActivityLoading &&
              !selectedActivityTree && (
                <div className="status">Select a message to view activity.</div>
              )}
          </div>
        )}
        <div className="runner-sidebar__steps">
          {selectedPreparationActivityGrouped.length > 0 && (
            <div className="runner-stage">
              <div className="runner-stage__header">
                <div className="runner-stage__title">Preparation timeline</div>
                <div className="runner-stage__meta">
                  {selectedPreparationActivityGrouped.length} events
                </div>
              </div>
              <div className="runner-stage__commands">
                {selectedPreparationActivityGrouped.map((event, index) => (
                  <div key={event.id} className="activity-log__line">
                    <span className="activity-log__timestamped">
                      <Timestamp
                        value={event.created_at}
                        variant="activity"
                        relativeTo={selectedPreparationActivityGrouped[index - 1]?.created_at}
                      />
                      {event.message ?? event.stage ?? event.event_name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {selectedQueueActivityGrouped.length > 0 && (
            <div className="runner-stage">
              <div className="runner-stage__header">
                <div className="runner-stage__title">Queue timeline</div>
                <div className="runner-stage__meta">
                  {selectedQueueActivityGrouped.length} events
                </div>
              </div>
              <div className="runner-stage__commands">
                {selectedQueueActivityGrouped.map((event, index) => (
                  <div
                    key={event.id}
                    className={`activity-log__line${
                      event.event_name === 'queue.failed' ? ' activity-log__line--error' : ''
                    }`}
                  >
                    <span className="activity-log__timestamped">
                      <Timestamp
                        value={event.created_at}
                        variant="activity"
                        relativeTo={selectedQueueActivityGrouped[index - 1]?.created_at}
                      />
                      {event.message ??
                        (event.event_name === 'queue.started'
                          ? 'Starting to process'
                          : event.event_name)}
                    </span>
                    {event.error && event.error !== event.message && (
                      <span className="activity-log__error"> · {event.error}</span>
                    )}
                    {event.event_name === 'queue.waiting' &&
                      event.blockers &&
                      event.blockers.length > 0 && (
                        <span>
                          {' · blockers: '}
                          {event.blockers.map((blocker, index) => (
                            <span key={`${blocker.conversation_id}:${blocker.message_id}`}>
                              {index > 0 ? ', ' : ''}
                              <Link
                                to={`/conversations/${blocker.conversation_id}`}
                                className="link-button"
                              >
                                {blocker.conversation_id}
                              </Link>
                              {`#${blocker.message_id}`}
                            </span>
                          ))}
                        </span>
                      )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {(() => {
            const messageIdKey = effectiveSelectedActivityMessageId ?? 'none';
            const allStages = selectedActivityTree?.stages ?? [];
            const stageVisibleCountKey = messageVisibleStageCountKey(messageIdKey);
            const visibleStageCount = Math.min(
              allStages.length,
              visibleActivityStagesByMessage[stageVisibleCountKey] ?? activityStagesPage
            );
            const hiddenStageCount = Math.max(0, allStages.length - visibleStageCount);

            return (
              <>
                {allStages.slice(0, visibleStageCount).map((stage) => {
                  const stageKey = stageExpandKey(messageIdKey, stage.id);
                  const stageCommandCountKey = stageVisibleCommandCountKey(
                    messageIdKey,
                    stage.id
                  );
                  const isStageExpanded = expandedActivityStages[stageKey] ?? true;
                  const visibleCommandCount = Math.min(
                    stage.commands.length,
                    visibleActivityCommandsByStage[stageCommandCountKey] ??
                      activityStageCommandsPage
                  );
                  const hiddenCommandCount = Math.max(
                    0,
                    stage.commands.length - visibleCommandCount
                  );

                  return (
                    <div key={stage.id} className="runner-stage">
                      <button
                        type="button"
                        className="runner-stage__header"
                        onClick={() => onToggleStage(stageKey, isStageExpanded)}
                      >
                        <div className="runner-stage__title">
                          {stageLabelForId(stage.id, stage.name)}
                        </div>
                        <div className="runner-stage__meta">
                          {visibleCommandCount < stage.commands.length
                            ? `${visibleCommandCount}/${stage.commands.length} commands`
                            : `${stage.commands.length} commands`}
                        </div>
                      </button>
                      {isStageExpanded && (
                        <div className="runner-stage__commands">
                          {stage.commands.slice(0, visibleCommandCount).map((command, index) => {
                            const statusClass =
                              command.status === 'failed'
                                ? 'failed'
                                : command.status === 'succeeded'
                                  ? 'succeeded'
                                  : 'running';
                            const commandKey = commandExpandKey(messageIdKey, command.id);
                            const outputVisibleKey = commandVisibleOutputLineCountKey(
                              messageIdKey,
                              command.id
                            );
                            const isCommandExpanded =
                              expandedActivityCommands[commandKey] ?? false;
                            const loadingOlder = loadingOlderCommandOutput[commandKey] ?? false;
                            const copyingRaw = copyingRawCommandOutput[commandKey] ?? false;
                            const nodePreview = extractActivityNodePreview(command);
                            const canCopyRawAgentOutput = Boolean(
                              selectedActivityRunId &&
                                effectiveSelectedActivityMessageId &&
                                isAgentContainerStep(command.step)
                            );
                            const visibleOutputCount = Math.min(
                              command.output_tail.length,
                              visibleActivityOutputLinesByCommand[outputVisibleKey] ??
                                activityOutputLinesPage
                            );
                            const hiddenLoadedOutputCount = Math.max(
                              0,
                              command.output_tail.length - visibleOutputCount
                            );
                            const visibleOutput = command.output_tail.slice(
                              Math.max(0, command.output_tail.length - visibleOutputCount)
                            );
                            const duration = formatActivityDuration(
                              command.started_at,
                              command.finished_at,
                              nowMs
                            );
                            const streamCounts = countOutputStreams(command.output_tail);
                            const fullOutputHref =
                              selectedActivityRunId && effectiveSelectedActivityMessageId
                                ? `/api/conversations/${conversationId}/runs/${selectedActivityRunId}/commands/${encodeURIComponent(command.id)}/output?limit=5000`
                                : null;
                            const workingDirectory =
                              command.working_directory ?? selectedActivityRunCwd ?? null;
                            const previousCommand = stage.commands[index - 1] ?? null;
                            const previousCommandAt =
                              previousCommand?.finished_at ?? previousCommand?.started_at ?? null;

                            return (
                              <div
                                key={command.id}
                                className={`runner-step runner-step--${statusClass}`}
                              >
                                <button
                                  type="button"
                                  className="runner-step__header"
                                  onClick={() =>
                                    onToggleCommand(
                                      commandKey,
                                      isCommandExpanded,
                                      outputVisibleKey,
                                      command.output_tail.length
                                    )
                                  }
                                >
                                  <div className="runner-step__meta">
                                    <span className="runner-step__dot" />
                                    <div className="runner-step__label">
                                      <div className="runner-step__title-row">
                                        <span className="runner-step__title">{command.step}</span>
                                        {command.stage && (
                                          <span className="runner-step__kind">
                                            {stageLabelForId(command.stage, command.stage)}
                                          </span>
                                        )}
                                        {nodePreview && (
                                          <span
                                            className="runner-step__title-preview"
                                            title={nodePreview}
                                          >
                                            {nodePreview}
                                          </span>
                                        )}
                                      </div>
                                      {command.command && (
                                        <div className="runner-step__command">
                                          {renderTextWithLinks(command.command)}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="runner-step__status">
                                    {humanizeRunnerStatus(command.status)}
                                    {command.started_at && (
                                      <Timestamp
                                        value={command.started_at}
                                        variant="activity"
                                        relativeTo={previousCommandAt}
                                      />
                                    )}
                                    {command.exit_code !== null &&
                                      command.exit_code !== undefined && (
                                        <span className="runner-step__exit">
                                          exit {command.exit_code}
                                        </span>
                                      )}
                                    {duration && (
                                      <span className="runner-step__exit">{duration}</span>
                                    )}
                                  </div>
                                </button>
                                {isCommandExpanded && (
                                  <div className="runner-step__output">
                                    <div className="runner-step__details">
                                      <div>
                                        <span>Command ID</span>
                                        <strong>{command.id}</strong>
                                      </div>
                                      {workingDirectory && (
                                        <div>
                                          <span>Working directory</span>
                                          <strong>{workingDirectory}</strong>
                                        </div>
                                      )}
                                      {command.started_at && (
                                        <div>
                                          <span>Started</span>
                                          <strong>
                                            <Timestamp value={command.started_at} variant="activity" />
                                          </strong>
                                        </div>
                                      )}
                                      {command.finished_at && (
                                        <div>
                                          <span>Ended</span>
                                          <strong>
                                            <Timestamp value={command.finished_at} variant="activity" />
                                          </strong>
                                        </div>
                                      )}
                                      {duration && (
                                        <div>
                                          <span>Duration</span>
                                          <strong>{duration}</strong>
                                        </div>
                                      )}
                                      <div>
                                        <span>Output</span>
                                        <strong>
                                          {command.output_count} lines
                                          {streamCounts.stdout > 0 || streamCounts.stderr > 0
                                            ? ` (${streamCounts.stdout} stdout, ${streamCounts.stderr} stderr loaded)`
                                            : ''}
                                        </strong>
                                      </div>
                                    </div>
                                    <div className="runner-step__toolbar">
                                      <span className="status">
                                        {visibleOutputCount < command.output_count
                                          ? `${visibleOutputCount}/${command.output_count} lines`
                                          : `${command.output_count} lines`}
                                      </span>
                                      <div className="runner-step__toolbar-actions">
                                        {canCopyRawAgentOutput && (
                                          <button
                                            type="button"
                                            className="ghost"
                                            onClick={() =>
                                              selectedActivityRunId &&
                                              effectiveSelectedActivityMessageId &&
                                              onCopyRawAgentCommandOutput(
                                                effectiveSelectedActivityMessageId,
                                                selectedActivityRunId,
                                                command.id
                                              )
                                            }
                                            disabled={
                                              copyingRaw ||
                                              !selectedActivityRunId ||
                                              !effectiveSelectedActivityMessageId
                                            }
                                          >
                                            {copyingRaw ? 'Copying…' : 'Copy raw'}
                                          </button>
                                        )}
                                        {command.service_log &&
                                          selectedActivityRunId &&
                                          effectiveSelectedActivityMessageId && (
                                            <a
                                              className="ghost"
                                              href={`/api/conversations/${conversationId}/runs/${selectedActivityRunId}/service-logs/${command.service_log.id}`}
                                            >
                                              Download log
                                            </a>
                                          )}
                                        {fullOutputHref && command.output_count > 0 && (
                                          <a className="ghost" href={fullOutputHref} target="_blank" rel="noreferrer">
                                            Full output
                                          </a>
                                        )}
                                        {hiddenLoadedOutputCount > 0 && (
                                          <button
                                            type="button"
                                            className="ghost"
                                            onClick={() =>
                                              onShowMoreOutput(
                                                outputVisibleKey,
                                                command.output_tail.length
                                              )
                                            }
                                          >
                                            Show{' '}
                                            {Math.min(
                                              hiddenLoadedOutputCount,
                                              activityOutputLinesPage
                                            )}{' '}
                                            older
                                          </button>
                                        )}
                                        {(loadingOlder || command.has_more_output) && (
                                          <button
                                            type="button"
                                            className="ghost"
                                            onClick={() =>
                                              selectedActivityRunId &&
                                              effectiveSelectedActivityMessageId &&
                                              onLoadOlderCommandOutput(
                                                effectiveSelectedActivityMessageId,
                                                selectedActivityRunId,
                                                command.id
                                              )
                                            }
                                            disabled={
                                              !command.has_more_output ||
                                              loadingOlder ||
                                              !selectedActivityRunId ||
                                              !effectiveSelectedActivityMessageId
                                            }
                                          >
                                            {loadingOlder ? 'Loading…' : 'Fetch older'}
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    <div className="runner-step__viewer" aria-label="Output viewer">
                                      <div className="runner-step__viewer-title">Output viewer</div>
                                      {command.command && (
                                        <div className="runner-step__line runner-step__line--command">
                                          <span className="runner-step__line-stream">cmd</span>
                                          <span className="runner-step__line-text">
                                            {command.command}
                                          </span>
                                        </div>
                                      )}
                                      {visibleOutput.length > 0 ? (
                                        visibleOutput.map((output) => (
                                          <div
                                            key={output.id}
                                            className={`runner-step__line runner-step__line--${output.stream}`}
                                          >
                                            <span className="runner-step__line-stream">
                                              {output.stream}
                                            </span>
                                            <span className="runner-step__line-text">
                                              {output.text}
                                            </span>
                                          </div>
                                        ))
                                      ) : (
                                        <div className="runner-step__empty">
                                          No output captured.
                                        </div>
                                      )}
                                      {command.finished_at && (
                                        <div className="runner-step__line runner-step__line--status">
                                          <span className="runner-step__line-stream">status</span>
                                          <span className="runner-step__line-text">
                                            {humanizeRunnerStatus(command.status)} at{' '}
                                            <Timestamp value={command.finished_at} variant="activity" />
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {hiddenCommandCount > 0 && (
                            <button
                              type="button"
                              className="ghost runner-stage__more"
                              onClick={() =>
                                onShowMoreCommands(stageCommandCountKey, stage.commands.length)
                              }
                            >
                              Show {Math.min(hiddenCommandCount, activityStageCommandsPage)} more
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {hiddenStageCount > 0 && (
                  <button
                    type="button"
                    className="ghost runner-sidebar__more"
                    onClick={() => onShowMoreStages(stageVisibleCountKey, allStages.length)}
                  >
                    Show {Math.min(hiddenStageCount, activityStagesPage)} more stages
                  </button>
                )}
                {selectedActivityTree?.has_more_commands &&
                  effectiveSelectedActivityMessageId &&
                  selectedActivityRunId && (
                    <button
                      type="button"
                      className="ghost runner-sidebar__more"
                      onClick={() =>
                        onLoadOlderActivityCommands(
                          effectiveSelectedActivityMessageId,
                          selectedActivityRunId
                        )
                      }
                      disabled={selectedActivityLoadingOlder}
                    >
                      {selectedActivityLoadingOlder
                        ? 'Loading older activity…'
                        : 'Load older activity'}
                    </button>
                  )}
              </>
            );
          })()}
        </div>
      </aside>
    </>
  );
}
